import moment, { type MomentInput } from 'moment';

/**
 * Canonical date formats for the app. There are exactly two, one per boundary:
 *
 * - {@link API_DATE_FORMAT} (`YYYY-MM-DD`, ISO-8601) — the ONE format dates cross
 *   the API boundary in. It matches the value of a native `<input type="date">`
 *   and the GraphQL `Date` scalar, so form state flows straight to the wire with
 *   no reformatting. Every mutation/query date variable goes through
 *   {@link toApiDate}.
 * - {@link DISPLAY_DATE_FORMAT} (`MM/DD/YYYY`) — human-facing display only. Never
 *   sent to the API. Use {@link formatDisplayDate}.
 *
 * Mixing the two on the wire is what produced the historical
 * `Moment<Invalid date>` crash; keep wire dates ISO and display dates US-format.
 */
export const API_DATE_FORMAT = 'YYYY-MM-DD';

export const DISPLAY_DATE_FORMAT = 'MM/DD/YYYY';

/**
 * Parse any date-ish value the way {@link formatDisplayDate} does — strictly,
 * at UTC. Inline editors seed their picker from this so the value shown and the
 * value being edited can never disagree about which calendar day it is; a loose
 * `moment(value)` parses at local time and slides a day for viewers behind UTC.
 * @param value
 */
export function parseDateValue(value?: MomentInput): moment.Moment | null {
  return toMoment(value as MomentInput);
}

function toMoment(value: MomentInput): moment.Moment | null {
  if (value === null || value === undefined || value === '') {
    return null;
  }
  // A Moment carries its own (often local) zone — format it as-is so a date
  // picked in local time keeps the calendar day the user chose.
  if (moment.isMoment(value)) {
    return value.isValid() ? value : null;
  }
  // Strings are date-only domain values; parse strictly at UTC so a plain
  // `YYYY-MM-DD` (or legacy `MM/DD/YYYY`) never shifts a day across zones.
  const parseFormats: moment.MomentFormatSpecification = [
    moment.ISO_8601,
    'MM/DD/YYYY',
    'M/D/YYYY',
  ];
  const parsed =
    typeof value === 'string'
      ? moment.utc(value, parseFormats, true)
      : moment.utc(value);
  return parsed.isValid() ? parsed : null;
}

/**
 * Format any date-ish value to the canonical API wire format (`YYYY-MM-DD`).
 * Accepts ISO strings, the legacy `MM/DD/YYYY`, `Date`, epoch millis, or a
 * `Moment`. Returns `null` for empty/invalid input. Use for every mutation /
 * query date variable.
 * @param value
 */
export function toApiDate(value?: MomentInput): string | null {
  const m = toMoment(value as MomentInput);
  return m ? m.format(API_DATE_FORMAT) : null;
}

/**
 * Format any date-ish value for human display (`MM/DD/YYYY`). Returns `''` for
 * empty/invalid input. Never send the result to the API.
 * @param value
 */
export function formatDisplayDate(value?: MomentInput): string {
  const m = toMoment(value as MomentInput);
  return m ? m.format(DISPLAY_DATE_FORMAT) : '';
}
